import { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import { BodilColor } from '~src/branding';
import { BookingForm, BookingFormVariant } from '~src/components/BookingForm';

import { PartnerLogosSection } from './PartnerLogosSection';

type Props = {
  defaultTitle: ReactNode;
  style?: CSSProperties;
  variant?: BookingFormVariant;
};

const OfferTitle = styled.div({
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
});

const TrustpilotMark = styled.a({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '44px',
  padding: '8px 20px',
  borderRadius: '999px',
  background: BodilColor.base.white,
  color: BodilColor.base.default,
  fontSize: '22px',
  fontWeight: 800,
  letterSpacing: 0,
  boxShadow: '0 6px 22px rgba(1, 64, 65, 0.08)',
  textDecoration: 'none',
});

const TrustpilotStar = styled.span({
  color: '#00B67A',
  marginRight: '8px',
});

const OfferKicker = styled.div({
  color: BodilColor.ui.action.default,
  fontSize: '13px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
});

const OfferHeading = styled.h2({
  color: BodilColor.base.default,
  fontSize: 'clamp(28px, 3vw, 42px)',
  fontWeight: 800,
  lineHeight: 1.08,
  margin: 0,
});

const OfferText = styled.p({
  color: '#4F6B6A',
  fontSize: '16px',
  lineHeight: 1.45,
  margin: 0,
  maxWidth: '34rem',
});

const TrustLine = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '12px',
  width: '100%',
  marginTop: '18px',
});

const TrustItem = styled.div({
  borderRadius: '16px',
  background: BodilColor.base.white,
  border: `1px solid ${BodilColor.ui.action['20']}`,
  padding: '16px',
  textAlign: 'left',
});

const TrustItemTitle = styled.div({
  color: BodilColor.base.default,
  fontSize: '16px',
  fontWeight: 800,
  lineHeight: 1.2,
});

const TrustItemText = styled.div({
  color: '#4F6B6A',
  fontSize: '13px',
  lineHeight: 1.35,
  marginTop: '6px',
});

const PanelFooter = styled.div({
  marginTop: '18px',
  width: '100%',
});

export const EstimateBookingPanel = ({ defaultTitle, style, variant = 'default' }: Props) => {
  if (variant === 'offerIdentity') {
    return (
      <>
        <BookingForm
          variant={variant}
          style={style}
          title={
            <>
              <OfferTitle>
                <TrustpilotMark href='https://dk.trustpilot.com/review/bodil.energy' target='_blank' rel='noreferrer'>
                  <TrustpilotStar>★</TrustpilotStar>
                  Trustpilot
                </TrustpilotMark>
                <OfferKicker>Personlig energirådgivning</OfferKicker>
                <OfferHeading>Få et konkret oplæg til dit hjem</OfferHeading>
                <OfferText>
                  En Bodil-rådgiver vurderer din bolig, dit forbrug og dine muligheder, så du får en løsning der passer
                  til netop dit hus.
                </OfferText>
              </OfferTitle>

              <TrustLine>
                <TrustItem>
                  <TrustItemTitle>Lavere elregning</TrustItemTitle>
                  <TrustItemText>Vi beregner potentialet ud fra din adresse og dit forbrug.</TrustItemText>
                </TrustItem>
                <TrustItem>
                  <TrustItemTitle>Vi klarer forløbet</TrustItemTitle>
                  <TrustItemText>Fra beregning og tilbud til finansiering, koordinering og installation.</TrustItemText>
                </TrustItem>
              </TrustLine>
            </>
          }
        />

        <PanelFooter>
          <PartnerLogosSection variant='offerIdentity' />
        </PanelFooter>
      </>
    );
  }

  return (
    <>
      <BookingForm style={style} title={defaultTitle} />

      <PartnerLogosSection />
    </>
  );
};
